import { ComponentRef, inject, Injectable, Type } from '@angular/core';
import { ICartCreationDialogService } from 'shared/services/ui/cart-creation-dialog/cart-creation-dialog-service.abstract';
import { EError, EShoppingCartGetQueryType, ShoppingCartDto, ShoppingCartSetOrderDataCommand } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { DialogBaseService } from 'projects/shared/lib/services/ui/dialog.service';
import { CartCreationDialogBaseComponent } from '../../../components/shared/cart/cart-creation-dialog/cart-creation-dialog-base.component';
import { ServiceLocator } from '../../infrastructure/service-locator.service';
import { ICartService, ICartServiceToken } from '../../data/cart/cart-service.interface';
import { firstValueFrom } from 'rxjs';
import { ToastItem } from '../../../components/shared/toast/item/toast-item.model';
import { EToastItemType } from '../../../enum/toast/toast-entry-type.enum';
import { ErrorCodeService } from '../../infrastructure/error-code.service';
import { INavigationService, INavigationServiceToken } from '../../infrastructure/navigation/navigation-service.interface';
import { ILoggerServiceToken } from '../../infrastructure/logger/logger-service.interface';
import { IToastService, IToastServiceToken } from '../toast/toast-service.interface';

@Injectable({
  providedIn: 'root'
})
export class CartCreationDialogBaseService<T extends CartCreationDialogBaseComponent> implements ICartCreationDialogService {
  private readonly navigationService: INavigationService = inject(INavigationServiceToken);
  private readonly cartService: ICartService = inject(ICartServiceToken);
  private readonly toastService: IToastService = inject(IToastServiceToken);

  protected getComponent(): Type<any> {
    return CartCreationDialogBaseComponent;
  }

  public cartCreationDialogShow(
    callbackCancel?: () => void,
    cart?: ShoppingCartDto | undefined,
    isCartCreation: boolean | undefined = true,
  ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const component = this.getComponent();

    DialogBaseService.dialogGenericShow<T>(
      component,
      (component: ComponentRef<T>) => {
        component.instance.modalCancel.subscribe(
          () => {
            if (callbackCancel != undefined) {
              callbackCancel();
            }
            DialogBaseService.dynamicComponentRemove(component);
          }
        );

        let cartCopy = new ShoppingCartDto(cart);
        component.instance.cart = cartCopy;
        component.instance.isCartCreation = isCartCreation;
        component.instance.cartCreateClicked.subscribe(
          async (cartNew) => {
            let command = new ShoppingCartSetOrderDataCommand({
              cartNumber: cartNew.cartNumber,
              comment: cartNew.comment,
              deliveryDate: cartNew.deliveryDate ? new Date(cartNew.deliveryDate) : undefined,
              deliveryType: cartNew.deliveryType,
              deliveryAddressIdentifier: cartNew.deliveryAddressData?.addressIdentifier,
              customerOrderNumber: cartNew.customerOrderNumber,
            });
            try {
              await this.cartService.cartSetOrderDataAsync(command).then(
                async () => {
                  let deliveryDateHasChanged = cart?.deliveryDate != cartNew.deliveryDate;
                  let deliveryAddressHasChanged = cart?.deliveryAddressData?.addressIdentifier?.addressNumber != cartNew.deliveryAddressData?.addressIdentifier?.addressNumber;

                  if (deliveryDateHasChanged || deliveryAddressHasChanged) {
                    let cartLoaded = await firstValueFrom(this.cartService.cartGet(EShoppingCartGetQueryType.GetSpecific, true, cartNew.cartNumber));
                    if (cartLoaded?.shoppingCart != undefined) {
                      this.cartService.cartCurrentReplaceBy(cartLoaded.shoppingCart);
                      this.cartService.cartValidationStateSet(cartLoaded.shoppingCart, cartLoaded.shoppingCartValidationResult);
                    }
                  } else {
                    this.cartService.cartCurrentReplaceBy(cartNew);
                    firstValueFrom(this.cartService.cartValidate(cartNew.cartNumber)).then(
                      (validationResult) => {
                        this.cartService.cartValidationStateSet(cartNew, validationResult.shoppingCartValidationResult);
                      }
                    );
                  }

                  DialogBaseService.dynamicComponentRemove(component);
                }
              );
            } catch (error) {
              ServiceLocator.injector.get(ILoggerServiceToken).error(error, this.constructor.name);
              this.toastService.toastItemAdd(new ToastItem({
                title: $localize`:@@CartCreationDialogSaveFailureTitle:Cart could not be saved.`,
                message: $localize`:@@CartCreationDialogSaveFailureMessage:Your cart could not be saved. Please verify all fields and retry.`,
                type: EToastItemType.Danger,
                code: ErrorCodeService.formatErrorCode(EError.F170041)
              }));
            } finally {
              this.navigationService.refresh();
            }
          }
        );
      }
    );
  }

}
